import { Component, computed, effect, inject, signal } from '@angular/core';
import { createForm } from '@sonofabit/ng-core';
import { NbToastrService } from '@nebular/theme';
import { Validators } from '@angular/forms';
import { PendingChanges } from '@guards/pending-changes.guard';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { LabelDetailDto, LabelMergeDto, LabelService, } from '@artemis-software/wr-api';
import { tryWithLoading } from '@/utils/async-utils';
import CategoryEnum = LabelDetailDto.CategoryEnum;

@Component({
  standalone: false,
  selector: 'wr-label-detail-page',
  templateUrl: './label-detail-page.component.html',
  styleUrl: './label-detail-page.component.scss'
})
export class LabelDetailPageComponent implements PendingChanges {
  private readonly router = inject(Router);
  private readonly labelService = inject(LabelService);
  private readonly nbToastrService = inject(NbToastrService);

  loading$ = new BehaviorSubject(false);
  labelId: string | null = null;
  isEditing = false;

  form?: ReturnType<typeof this.createFormFromDetail>;

  firstSubmit = false;

  readonly canEdit = signal(false);
  readonly isNew = computed(() => !!(this.labelId && this.labelId === 'new'));
  readonly assignedSensorCount = signal<number | null>(null);
  readonly dimensionLocked = computed(() => (this.assignedSensorCount() ?? 0) > 0);
  readonly dimensionLockedHint = computed(() => {
    const count = this.assignedSensorCount() ?? 0;
    return `Wird von ${count} ${count === 1 ? 'Sensor' : 'Sensoren'} verwendet – Dimension nicht änderbar.`;
  });

  constructor() {
    effect(() => {
      if (this.dimensionLocked() && this.isEditing) {
        this.form?.get('dimension')?.disable();
      }
    });
    const route = inject(ActivatedRoute);
    route.paramMap.subscribe(async (params) => {
      const id = this.labelId = params.get('id');
      if (id) {
        if (id === 'new') {
          this.isEditing = true;
          this.canEdit.set(true);
          this.form = this.createFormFromDetail(null);
        } else {
          this.labelService.findById(id).subscribe((label) => {
            this.canEdit.set(label.permissions.canEdit);
            this.form = this.createFormFromDetail(label);
            this.form?.disable();
          });
          this.labelService.getAssignedSensorCount(id).subscribe((count) => {
            this.assignedSensorCount.set(count);
          });
        }
      } else {
        await this.router.navigate(['labels']);
      }
    });
  }

  createFormFromDetail(detail: LabelDetailDto | null) {
    return createForm(({ field }) => {
      return {
        id: field({ value: detail?.id }),
        shortcut: field({ value: detail?.shortcut, validators: [Validators.required] }),
        dimension: field({ value: detail?.dimension, validators: [Validators.required] }),
        category: field({
          value: detail?.category === CategoryEnum.None ? undefined : detail?.category,
          validators: [Validators.required],
        }),
        name: field({ value: detail?.name, validators: [Validators.required] }),
        description: field({ value: detail?.description }),
        color: field({ value: detail?.color, validators: [Validators.required] }),
        isVisible: field({ value: !!detail?.isVisible }),
      };
    });
  }

  navigateBack(): void {
    window.history.back();
  }

  onEdit(): void {
    this.isEditing = true;
    this.form?.enable();
    if (this.dimensionLocked()) {
      this.form?.get('dimension')?.disable();
    }
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.form?.markAsTouched();

    if (!this.form?.valid) {
      this.nbToastrService.danger('Label kann nicht gespeichert werden, es gibt noch Fehler.', 'Label');
      return;
    }

    await tryWithLoading(this.loading$, async () => {
      try {
        const labelMergeDto = {
          ...this.form?.value,
        } as LabelMergeDto;
        const saved = await firstValueFrom(this.labelService.merge(labelMergeDto));
        this.form?.get('id')?.setValue(saved.id);
        await this.reloadForm();
      } catch (e: unknown) {
        // Every failure in here comes from an HTTP call, and ErrorInterceptor already toasts those.
        // Toasting again showed the same backend message twice under two different titles.
        console.error(e);
      }
    });
  }

  async reloadForm(): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      const id = this.form?.value.id;
      if (id) {
        const label = await firstValueFrom(this.labelService.findById(id));
        this.form = this.createFormFromDetail(label);
      }
    });
    this.isEditing = false;
    this.form?.disable();
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  cancel(): void {
    window.history.back();
  }

  protected readonly signal = signal;
}
